"use client"

import { useState } from "react"
import { calculateFertilizerRecommendations, type FertilizerResult } from "@/lib/fertilizer-calculator"
import { getCurrentSeason } from "@/lib/date-utils"
import { analytics } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { Leaf, ArrowRight, RotateCcw } from "lucide-react"
import Link from "next/link"

const GRASS_TYPES = [
  { value: "Kentucky Bluegrass", label: "Kentucky Bluegrass" },
  { value: "Bermudagrass", label: "Bermuda" },
  { value: "St. Augustinegrass", label: "St. Augustine" },
  { value: "Zoysiagrass", label: "Zoysia" },
  { value: "Tall Fescue", label: "Tall Fescue" },
  { value: "Fine Fescue", label: "Fine Fescue" },
  { value: "Perennial Ryegrass", label: "Ryegrass" },
  { value: "Centipede", label: "Centipede" },
  { value: "Bahiagrass", label: "Bahia" },
  { value: "Buffalograss", label: "Buffalo" },
]

interface MiniFertilizerCalculatorProps {
  /** Article slug, threaded into the impression for per-post reads. */
  slug?: string
  /** Marker data-variant; "blog-intro" is the injected intro placement. */
  variant?: string
}

/**
 * Embedded fertilizer-rate widget.
 *
 * Instrumented to the same contract as MiniHerbicideTiming: this component
 * shipped with no analytics at all, so its hand-authored Directus embeds were
 * invisible — the widened intro rollout (FERTILIZER_TOOL_POSTS in
 * lib/content-processor.ts) would have been unreadable. The impression fires on
 * the shared `cta_surface_shown` contract and a completed calculation fires
 * `calculator_used` from the intentional `form_submit` source.
 */
export function MiniFertilizerCalculator({ slug, variant }: MiniFertilizerCalculatorProps = {}) {
  const [lawnSize, setLawnSize] = useState("")
  const [grassType, setGrassType] = useState("")
  const [result, setResult] = useState<FertilizerResult | null>(null)
  const [error, setError] = useState("")

  const ctaLocation = variant === "blog-intro" ? "blog_intro_fertilizer_widget" : "blog_inline_fertilizer_widget"
  const impressionRef = useImpressionEvent<HTMLDivElement>(
    ctaLocation,
    { surface: "mini_fertilizer_calculator", article_slug: slug, slot: variant },
    { emit: analytics.ctaSurfaceShown },
  )

  const handleCalculate = () => {
    setError("")
    const size = parseInt(lawnSize)
    if (!size || size < 100 || size > 100000) {
      setError("Enter a lawn size between 100 and 100,000 sq ft")
      return
    }
    if (!grassType) {
      setError("Select your grass type")
      return
    }

    try {
      const season = getCurrentSeason()
      // Calculates for balanced 10-10-10 fertilizer as a general-purpose baseline.
      // Users wanting product-specific rates are directed to the full calculator.
      const calcResult = calculateFertilizerRecommendations({
        lawnSize: size,
        grassType,
        fertilizerType: "balanced-10-10-10",
        season,
        applicationGoal: "maintenance",
        soilType: "loamy",
      })

      if ((calcResult as any).error) {
        setError((calcResult as any).error)
        return
      }

      setResult(calcResult)
      analytics.calculatorUsed('fertilizer', {
        inputs: { lawn_size: size, grass_type: grassType },
        source: 'form_submit',
      })
    } catch {
      setError("Could not calculate. Please try again.")
    }
  }

  const handleReset = () => {
    setLawnSize("")
    setGrassType("")
    setResult(null)
    setError("")
  }

  return (
    <div ref={impressionRef} className="my-8 mx-auto max-w-md rounded-xl border-2 border-green-200 bg-linear-to-b from-green-50/80 to-white overflow-hidden shadow-xs inline-widget">
      <div className="flex items-center gap-2 px-5 py-3 bg-green-600">
        <Leaf className="w-4 h-4 text-white" />
        <h3 className="text-sm font-bold text-white">Quick Fertilizer Calculator</h3>
      </div>

      {!result ? (
        <div className="p-5 space-y-3">
          <div>
            <label htmlFor="mini-fert-size" className="block text-sm font-medium text-gray-700 mb-1">
              Lawn Size (sq ft)
            </label>
            <input
              id="mini-fert-size"
              type="number"
              placeholder="e.g., 5000"
              value={lawnSize}
              onChange={(e) => setLawnSize(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-gray-300 text-base focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label htmlFor="mini-fert-grass" className="block text-sm font-medium text-gray-700 mb-1">
              Grass Type
            </label>
            <select
              id="mini-fert-grass"
              value={grassType}
              onChange={(e) => setGrassType(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-gray-300 text-sm focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
            >
              <option value="">Select grass type</option>
              {GRASS_TYPES.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>

          {error && <p className="text-red-600 text-xs">{error}</p>}

          <button
            onClick={handleCalculate}
            className="w-full h-10 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            Calculate
          </button>
        </div>
      ) : (
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-3 bg-green-50 rounded-lg border border-green-100">
              <p className="text-xs text-gray-500 mb-1">Amount Needed</p>
              <p className="text-lg font-bold text-green-700">{result.fertilizerAmount.pounds.toFixed(1)} lbs</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg border border-green-100">
              <p className="text-xs text-gray-500 mb-1">Bags to Buy</p>
              <p className="text-lg font-bold text-green-700">{result.fertilizerAmount.bags}</p>
              <p className="text-xs text-gray-400">{result.fertilizerAmount.bagSize} lb bags</p>
            </div>
          </div>

          <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Estimated Cost</p>
            <p className="text-base font-bold text-gray-800">${result.fertilizerAmount.costEstimate.toFixed(2)}</p>
          </div>

          <p className="text-xs text-gray-500 text-center">
            Based on balanced 10-10-10 fertilizer. {result.applicationDetails.timing}
          </p>

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleReset}
              className="h-9 flex items-center justify-center gap-1 border border-gray-300 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RotateCcw className="w-3 h-3" /> Retry
            </button>
            <Link
              href="/tools/fertilizer-calculator"
              className="h-9 flex items-center justify-center gap-1 bg-green-600 text-white text-xs font-medium no-underline rounded-lg hover:bg-green-700 transition-colors"
            >
              Full Calculator <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
